import { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Crown, DiscordLogo, Lightning, Play, Vault } from "@phosphor-icons/react";
import AudioRow from "../AudioRow";
import LazyImage from "../LazyImage";
import { usePlayer } from "../../context/PlayerContext";
import { EASE, MaskTitle, Reveal } from "./motion";

export function SectionHead({ eyebrow, title, link, className = "" }) {
  return (
    <div className={`flex items-end justify-between gap-6 ${className}`}>
      <div>
        <Reveal y={16} className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-3">{eyebrow}</Reveal>
        <MaskTitle className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02]">{title}</MaskTitle>
      </div>
      {link && (
        <Reveal y={16} delay={0.2} className="hidden md:block flex-shrink-0">
          <Link to={link.to} className="group inline-flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">
            {link.label}
            <span className="w-9 h-9 rounded-full border border-white/15 group-hover:bg-white group-hover:text-black group-hover:border-white flex items-center justify-center transition-all duration-300">
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        </Reveal>
      )}
    </div>
  );
}

/* ---------------- Trending audios ---------------- */
function Equalizer() {
  const { isPlaying } = usePlayer();
  const bars = useMemo(
    () => Array.from({ length: 28 }, (_, i) => ({ h: 30 + ((Math.sin(i * 0.9) + 1) / 2) * 70, d: 0.9 + ((i * 37) % 10) / 10, delay: (i % 7) * -0.23 })),
    []
  );
  return (
    <div className="flex items-end gap-[3px] h-28 md:h-36" aria-hidden="true">
      {bars.map((b, i) => (
        <span
          key={i}
          className={`eq-bar flex-1 rounded-full bg-gradient-to-t from-[#880015] to-[#FF8090] transition-opacity duration-700 ${isPlaying ? "opacity-100" : "opacity-40"}`}
          style={{ height: `${b.h}%`, "--eq-d": `${isPlaying ? b.d * 0.7 : b.d * 2}s`, "--eq-delay": `${b.delay}s` }}
        />
      ))}
    </div>
  );
}

export function AudiosSection({ audios, eyebrow, title, subtitle }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] glow-garnet-md pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] gap-12 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start">
          <Reveal y={16} className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-3">{eyebrow}</Reveal>
          <MaskTitle className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02] mb-6">{title}</MaskTitle>
          <Reveal delay={0.15}><p className="text-white/55 leading-relaxed mb-8 max-w-sm">{subtitle}</p></Reveal>
          <Reveal delay={0.25} className="mb-10">
            <Link to="/audios" className="btn-primary" data-testid="home-hear-all">Hear all <ArrowRight size={16} /></Link>
          </Reveal>
          <Reveal delay={0.3} y={50}><Equalizer /></Reveal>
        </div>

        <div className="space-y-2.5">
          {audios.map((a, i) => (
            <Reveal key={a.id} x={48} y={0} delay={Math.min(i, 5) * 0.07} className="transition-transform duration-500 hover:translate-x-1.5">
              <AudioRow audio={a} queue={audios} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Edits in the wild (scroll-parallax columns) ---------------- */
function EditCard({ edit }) {
  return (
    <Link to="/edits" className="group relative block aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 bg-[#1A1A1A] transition-colors duration-500">
      <LazyImage
        src={edit.thumbnail_url}
        width={480}
        alt={edit.title}
        className="absolute inset-0 w-full h-full"
        imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] transform-gpu"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
      <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 text-black flex items-center justify-center scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition duration-300">
        <Play size={14} weight="fill" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
        <div className="font-display font-bold text-sm md:text-base leading-tight line-clamp-2">{edit.title}</div>
        <div className="text-xs text-white/55 mt-1 font-mono">@{edit.creator_name}</div>
      </div>
    </Link>
  );
}

export function EditsSection({ edits, eyebrow, title }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const up = useTransform(scrollYProgress, [0, 1], [70, -70]);
  const down = useTransform(scrollYProgress, [0, 1], [-30, 40]);
  const upSmooth = useSpring(up, { stiffness: 90, damping: 24 });
  const downSmooth = useSpring(down, { stiffness: 90, damping: 24 });
  if (!edits.length) return null;

  return (
    <section ref={ref} className="relative max-w-[1600px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-24 md:pb-32">
      <SectionHead eyebrow={eyebrow} title={title} link={{ to: "/edits", label: "See all edits" }} className="mb-12" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 md:pb-16">
        {edits.map((e, i) => (
          <motion.div key={e.id} style={{ y: i % 2 ? downSmooth : upSmooth }} className={i % 2 ? "md:mt-16" : ""}>
            <Reveal y={60} delay={(i % 4) * 0.08}><EditCard edit={e} /></Reveal>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- AA+ teaser (cursor spotlight + orbiting border) ---------------- */
const PERK_ICONS = [Crown, Lightning, Vault, DiscordLogo];

export function AaPlusTeaser({ title, subtitle, perks }) {
  const mx = useMotionValue(50);
  const my = useMotionValue(30);
  const sx = useSpring(mx, { stiffness: 120, damping: 20 });
  const sy = useSpring(my, { stiffness: 120, damping: 20 });
  const glow = useMotionTemplate`radial-gradient(520px circle at ${sx}% ${sy}%, rgba(255,128,144,0.16), transparent 60%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 100);
    my.set(((e.clientY - r.top) / r.height) * 100);
  };

  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
      <Reveal y={60}>
        <div className="relative rounded-3xl p-px overflow-hidden" onMouseMove={onMove}>
          <div className="absolute -inset-[100%] animate-[spin_9s_linear_infinite] bg-[conic-gradient(from_0deg,transparent_0_72%,rgba(255,128,144,0.9)_86%,transparent_100%)]" />
          <div className="relative rounded-[23px] bg-[#131313] overflow-hidden grain">
            <motion.div className="absolute inset-0 pointer-events-none" style={{ background: glow }} />
            <div className="absolute -top-32 -right-24 w-96 h-96 glow-garnet-lg pointer-events-none" />
            <div className="relative p-8 md:p-16 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <div className="chip chip-garnet mb-5"><Crown size={12} weight="fill" /> AA+ Premium</div>
                <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] mb-5">
                  <span className="gradient-emphasis">{title}</span>
                </h2>
                <p className="text-white/65 leading-relaxed mb-8 max-w-md">{subtitle}</p>
                <Link to="/aa-plus" className="btn-primary" data-testid="aa-plus-cta">Explore AA+ <ArrowRight size={16} /></Link>
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {perks.map((f, i) => {
                  const Icon = PERK_ICONS[i % PERK_ICONS.length];
                  return (
                    <motion.div
                      key={f}
                      initial={{ opacity: 0, y: 30, scale: 0.96 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
                      transition={{ duration: 0.7, delay: 0.15 + i * 0.09, ease: EASE }}
                      whileHover={{ y: -6 }}
                      className="group relative rounded-2xl border border-white/10 hover:border-[#FF8090]/40 bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-md p-5 md:p-6 transition-colors duration-300"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#880015]/25 text-[#FF8090] flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-[-6deg] transition-transform duration-300">
                        <Icon size={20} weight="fill" />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090] mb-1">Perk</div>
                      <div className="text-sm md:text-base font-semibold leading-snug">{f}</div>
                      <ArrowUpRight size={14} className="absolute top-4 right-4 text-white/0 group-hover:text-white/60 transition-colors" />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
